import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Chip,
  InputAdornment,
  Avatar,
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import AddIcon        from "@mui/icons-material/Add";
import WorkIcon       from "@mui/icons-material/Work";
import SearchIcon     from "@mui/icons-material/Search";
import ClearIcon      from "@mui/icons-material/Clear";
import { Link } from "react-router-dom";
import { useAuth } from "../components/AuthContext";

const baseURL = process.env.REACT_APP_API_URL || "";

function inicialesNombre(nombre) {
  if (!nombre) return "?";
  return nombre.split(" ").slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

const EMPTY_EDIT = {
  nombre: "",
  apellido_paterno: "",
  apellido_materno: "",
  email: "",
  fecha_ingreso: "",
  estatus: "activo",
};

const EMPTY_PUESTO = {
  id_puesto: "",
  id_area: "",
  id_departamento: "",
  jefe_nomina: null,
};

const EMPTY_CREATE = {
  numero_nomina: "",
  nombre: "",
  apellido_paterno: "",
  apellido_materno: "",
  email: "",
  id_puesto: "",
  id_area: "",
  id_departamento: "",
  jefe_nomina: null,
  fecha_ingreso: "",
  estatus: "activo",
};

export default function Empleados() {
  const { hasPermission } = useAuth();

  const [empleados, setEmpleados] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, total_pages: 1 });

  const [q, setQ] = useState("");

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openPuesto, setOpenPuesto] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedNomina, setSelectedNomina] = useState("");

  const [puestos, setPuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [empleadosSelect, setEmpleadosSelect] = useState([]);

  const [createData, setCreateData] = useState(EMPTY_CREATE);
  const [editData, setEditData] = useState(EMPTY_EDIT);
  const [puestoData, setPuestoData] = useState(EMPTY_PUESTO);

  const cargar = useCallback(async (page = 1) => {
    try {
      const params = { page, page_size: meta.page_size, estatus: "activo" };
      if (q) params.q = q;
      const res = await api.get("/empleados", { params });
      setEmpleados(res.data.data);
      setMeta(res.data.meta);
    } catch (err) {
      console.error("Error loading employees:", err);
    }
  }, [meta.page_size, q]);

  const cargarCatalogos = useCallback(async () => {
    try {
      const [pRes, aRes, dRes, eRes] = await Promise.all([
        api.get("/puestos"),
        api.get("/areas"),
        api.get("/departamentos"),
        api.get("/empleados/select")
      ]);
      setPuestos(pRes.data);
      setAreas(aRes.data);
      setDepartamentos(dRes.data);
      setEmpleadosSelect(eRes.data);
    } catch (err) {
      console.error("Error loading catalogs:", err);
    }
  }, []);

  useEffect(() => {
    cargar(1);
    cargarCatalogos();
  }, [cargar, cargarCatalogos]);

  const fieldCreate = (key) => (e) => setCreateData(prev => ({ ...prev, [key]: e.target.value }));
  const fieldEdit   = (key) => (e) => setEditData(prev => ({ ...prev, [key]: e.target.value }));
  const fieldPuesto = (key) => (e) => setPuestoData(prev => ({ ...prev, [key]: e.target.value }));

  // ── Create ─────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createData.numero_nomina.trim() || !createData.nombre.trim() || !createData.apellido_paterno.trim())
      return alert("Payroll number, first name, and last name are required");
    if (!createData.id_puesto) return alert("Position is required");
    if (!createData.id_departamento) return alert("Department is required");

    try {
      await api.post("/empleados", {
        ...createData,
        id_area: createData.id_area || null,
        id_departamento: Number(createData.id_departamento),
        id_puesto: Number(createData.id_puesto),
        jefe_nomina: createData.jefe_nomina || null,
        fecha_ingreso: createData.fecha_ingreso || null,
      });
      setOpenCreate(false);
      setCreateData(EMPTY_CREATE);
      cargar(1);
    } catch (err) {
      alert("Error creating employee: " + (err.response?.data?.detail || err.message));
    }
  };

  // ── Edit basic details ─────────────────────────────────────────────────
  const openEditDialog = async (emp) => {
    setSelectedId(emp.id_empleado);
    setSelectedNomina(emp.Numero_nomina || "");
    try {
      const res = await api.get(`/empleados/${emp.id_empleado}`);
      const d = res.data;
      setEditData({
        nombre:           d.nombre           || "",
        apellido_paterno: d.apellido_paterno || "",
        apellido_materno: d.apellido_materno || "",
        email:            d.email            || "",
        fecha_ingreso:    d.fecha_ingreso    || "",
        estatus:          d.estatus          || "activo",
      });
    } catch {
      setEditData(EMPTY_EDIT);
    }
    setOpenEdit(true);
  };

  const handleEdit = async () => {
    if (!editData.nombre.trim() || !editData.apellido_paterno.trim())
      return alert("First name and last name are required");
    try {
      await api.put(`/empleados/${selectedId}`, {
        nombre:           editData.nombre,
        apellido_paterno: editData.apellido_paterno,
        apellido_materno: editData.apellido_materno || null,
        email:            editData.email || null,
        fecha_ingreso:    editData.fecha_ingreso || null,
        estatus:          editData.estatus,
      });
      setOpenEdit(false);
      setSelectedId(null);
      cargar(1);
    } catch (err) {
      alert("Error updating employee: " + (err.response?.data?.detail || err.message));
    }
  };

  // ── Change position / area / department ────────────────────────────────
  const openPuestoDialog = async (emp) => {
    setSelectedId(emp.id_empleado);
    setSelectedNomina(emp.Numero_nomina || "");
    try {
      const res = await api.get(`/empleados/${emp.id_empleado}`);
      const d = res.data;
      const deptObj = departamentos.find(dep => dep.nombre_departamento === emp.departamento_texto);
      setPuestoData({
        id_puesto:       d.id_puesto       || "",
        id_area:         d.id_area         || "",
        id_departamento: deptObj ? deptObj.id_departamento : "",
        jefe_nomina:     d.jefe_nomina     || null,
      });
    } catch {
      setPuestoData(EMPTY_PUESTO);
    }
    setOpenPuesto(true);
  };

  const handleCambioPuesto = async () => {
    if (!puestoData.id_puesto) return alert("Position is required");
    if (!puestoData.id_departamento) return alert("Department is required");
    try {
      await api.put(`/empleados/${selectedId}`, {
        id_puesto:       Number(puestoData.id_puesto),
        id_area:         puestoData.id_area ? Number(puestoData.id_area) : 0,
        id_departamento: Number(puestoData.id_departamento),
        jefe_nomina:     puestoData.jefe_nomina || 0,
      });
      setOpenPuesto(false);
      setSelectedId(null);
      cargar(1);
    } catch (err) {
      alert("Error updating position: " + (err.response?.data?.detail || err.message));
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Mark this employee as inactive?")) return;
    try {
      await api.delete(`/empleados/${id}`);
      cargar(1);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  // ── Create fields ──────────────────────────────────────────────────────
  const createFields = (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Payroll Number *" value={createData.numero_nomina} onChange={fieldCreate("numero_nomina")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="First Name(s) *" value={createData.nombre} onChange={fieldCreate("nombre")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Last Name *" value={createData.apellido_paterno} onChange={fieldCreate("apellido_paterno")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Mother's Last Name" value={createData.apellido_materno} onChange={fieldCreate("apellido_materno")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Email" value={createData.email} onChange={fieldCreate("email")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Start Date" type="date" value={createData.fecha_ingreso}
          onChange={fieldCreate("fecha_ingreso")} InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Position *</InputLabel>
          <Select value={createData.id_puesto} onChange={fieldCreate("id_puesto")} label="Position *">
            {puestos.map(p => <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Department *</InputLabel>
          <Select value={createData.id_departamento} onChange={fieldCreate("id_departamento")} label="Department *">
            {departamentos.map(d => <MenuItem key={d.id_departamento} value={d.id_departamento}>{d.nombre_departamento}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Area (optional)</InputLabel>
          <Select value={createData.id_area} onChange={fieldCreate("id_area")} label="Area (optional)">
            <MenuItem value=""><em>No area</em></MenuItem>
            {areas.map(a => <MenuItem key={a.id_area} value={a.id_area}>{a.nombre_area}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select value={createData.estatus} onChange={fieldCreate("estatus")} label="Status">
            <MenuItem value="activo">Active</MenuItem>
            <MenuItem value="inactivo">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={empleadosSelect}
          getOptionLabel={op => `${op.nombre_completo} (Payroll: ${op.Numero_nomina})`}
          isOptionEqualToValue={(op, val) => Number(op.Numero_nomina) === Number(val.Numero_nomina)}
          value={empleadosSelect.find(e => Number(e.Numero_nomina) === Number(createData.jefe_nomina)) || null}
          onChange={(_, v) => setCreateData(prev => ({ ...prev, jefe_nomina: v ? Number(v.Numero_nomina) : null }))}
          renderInput={params => <TextField {...params} label="Direct supervisor (optional)" placeholder="Search by name or payroll number..." />}
        />
      </Grid>
    </Grid>
  );

  // ── Edit fields (basic details) ────────────────────────────────────────
  const editFields = (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Payroll number:</Typography>
          <Chip label={selectedNomina} size="small" />
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="First Name(s) *" value={editData.nombre} onChange={fieldEdit("nombre")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Last Name *" value={editData.apellido_paterno} onChange={fieldEdit("apellido_paterno")} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Mother's Last Name" value={editData.apellido_materno} onChange={fieldEdit("apellido_materno")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Email" value={editData.email} onChange={fieldEdit("email")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Start Date" type="date" value={editData.fecha_ingreso}
          onChange={fieldEdit("fecha_ingreso")} InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select value={editData.estatus} onChange={fieldEdit("estatus")} label="Status">
            <MenuItem value="activo">Active</MenuItem>
            <MenuItem value="inactivo">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  // ── Change position fields ─────────────────────────────────────────────
  const puestoFields = (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Employee payroll #:</Typography>
          <Chip label={selectedNomina} size="small" />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel>Position *</InputLabel>
          <Select value={puestoData.id_puesto} onChange={fieldPuesto("id_puesto")} label="Position *">
            {puestos.map(p => <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel>Department *</InputLabel>
          <Select value={puestoData.id_departamento} onChange={fieldPuesto("id_departamento")} label="Department *">
            {departamentos.map(d => <MenuItem key={d.id_departamento} value={d.id_departamento}>{d.nombre_departamento}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Area (optional)</InputLabel>
          <Select value={puestoData.id_area} onChange={fieldPuesto("id_area")} label="Area (optional)">
            <MenuItem value=""><em>No area</em></MenuItem>
            {areas.map(a => <MenuItem key={a.id_area} value={a.id_area}>{a.nombre_area}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={empleadosSelect}
          getOptionLabel={op => `${op.nombre_completo} (Payroll: ${op.Numero_nomina})`}
          isOptionEqualToValue={(op, val) => Number(op.Numero_nomina) === Number(val.Numero_nomina)}
          value={empleadosSelect.find(e => Number(e.Numero_nomina) === Number(puestoData.jefe_nomina)) || null}
          onChange={(_, v) => setPuestoData(prev => ({ ...prev, jefe_nomina: v ? Number(v.Numero_nomina) : null }))}
          renderInput={params => <TextField {...params} label="Direct supervisor (optional)" placeholder="Search by name or payroll number..." />}
        />
      </Grid>
    </Grid>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Employees</Typography>
        {hasPermission("EMPLEADOS_CREAR") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateData(EMPTY_CREATE); setOpenCreate(true); }}>
            Create Employee
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name, payroll number, position, area..."
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && cargar(1)}
          sx={{ flex: 1, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: q ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQ("")} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button variant="contained" onClick={() => cargar(1)}>Search</Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Payroll #</TableCell>
            <TableCell sx={{ color: "white" }}>Name</TableCell>
            <TableCell sx={{ color: "white" }}>Position</TableCell>
            <TableCell sx={{ color: "white" }}>Area</TableCell>
            <TableCell sx={{ color: "white" }}>Department</TableCell>
            <TableCell sx={{ color: "white" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {empleados.map(emp => (
            <TableRow key={emp.id_empleado}>
              <TableCell>{emp.Numero_nomina}</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={emp.foto_url ? `${baseURL}/${emp.foto_url}` : undefined}
                    sx={{ width: 36, height: 36, bgcolor: "#C62828", fontSize: 13, fontWeight: 700 }}
                  >
                    {inicialesNombre(emp.nombre_completo)}
                  </Avatar>
                  <Typography variant="body2">{emp.nombre_completo}</Typography>
                </Box>
              </TableCell>
              <TableCell>{emp.puesto_texto}</TableCell>
              <TableCell>{emp.area_texto}</TableCell>
              <TableCell>{emp.departamento_texto}</TableCell>
              <TableCell>
                <IconButton component={Link} to={`/empleado/${emp.id_empleado}`} title="View profile">
                  <VisibilityIcon />
                </IconButton>
                {hasPermission("EMPLEADOS_EDITAR") && (
                  <IconButton onClick={() => openEditDialog(emp)} title="Edit personal details">
                    <EditIcon />
                  </IconButton>
                )}
                {hasPermission("EMPLEADOS_EDITAR") && (
                  <IconButton onClick={() => openPuestoDialog(emp)} title="Change position / area / department" color="primary">
                    <WorkIcon />
                  </IconButton>
                )}
                {hasPermission("EMPLEADOS_ELIMINAR") && (
                  <IconButton onClick={() => handleDelete(emp.id_empleado)} color="error" title="Deactivate">
                    <DeleteIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={meta.total}
        page={meta.page - 1}
        onPageChange={(_, newPage) => cargar(newPage + 1)}
        rowsPerPage={meta.page_size}
        onRowsPerPageChange={e => setMeta(prev => ({ ...prev, page_size: parseInt(e.target.value), page: 1 }))}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      />

      {/* Create dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Employee</DialogTitle>
        <DialogContent>{createFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit basic details dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Personal Details</DialogTitle>
        <DialogContent>{editFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Change position dialog */}
      <Dialog open={openPuesto} onClose={() => setOpenPuesto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Position / Area / Department</DialogTitle>
        <DialogContent>{puestoFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPuesto(false)}>Cancel</Button>
          <Button onClick={handleCambioPuesto} variant="contained" color="primary">Change</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
